import {
  ElementType,
  Fixture as FixtureElement,
  Rect,
  Symbol as SymbolElement,
  Text,
  Trapezoid,
  Triangle,
} from '@ritabygglov/core';
import { TEXT_MIN_HEIGHT, TEXT_MIN_WIDTH } from '../../../constants';
import {
  clampRotatedRectSize,
  closestAlignedPoint,
  copy,
  getAdjacentPointIndices,
  getMidPoints,
  resizeRect,
  rotateCenter,
  snapPointToAngle,
} from '../../../helpers/utility';
import { computeTrapezoidVertices } from '../../../services/Actions/Trapezoid';
import { ResizeAnchor } from '../../../types';
import { applySelfSnap, syncConnectedPoints } from '../helpers';
import {
  ApexDragContext,
  ElementHandler,
  ResizeContext,
  RotateContext,
} from '../types/interactionTypes';

class RectHandler implements ElementHandler {
  canHandle(elementType: ElementType): boolean {
    return [ElementType.Rect, ElementType.Text].includes(elementType);
  }

  handleResize(context: ResizeContext) {
    const { element, coordinates, selectedAnchor, minHeight = 0 } = context;
    const el = copy(element) as Rect | Text;

    let [width, height, x, y] = resizeRect(
      selectedAnchor as ResizeAnchor,
      coordinates.x,
      coordinates.y,
      el
    );

    if (el.type === ElementType.Text) {
      [width, height, x, y] = clampRotatedRectSize(
        width,
        height,
        x,
        y,
        el.rotate,
        selectedAnchor as ResizeAnchor,
        TEXT_MIN_WIDTH,
        Math.max(TEXT_MIN_HEIGHT, minHeight)
      );
    }

    return {
      ...el,
      width,
      height,
      x,
      y,
    };
  }

  handleRotate(context: RotateContext) {
    const { element, coordinates, isShiftPressed } = context;
    const el = copy(element);
    const [midX, midY] = getMidPoints(el);
    const deltaX = coordinates.x - midX;
    const deltaY = coordinates.y - midY;

    const angle = (Math.atan2(deltaY, deltaX) * 180) / Math.PI + 90;
    const snapIncrement = 15;

    el.rotate = isShiftPressed
      ? Math.round(angle / snapIncrement) * snapIncrement
      : Math.round(angle);

    return el;
  }
}

class TrapezoidHandler implements ElementHandler {
  canHandle(elementType: ElementType): boolean {
    return elementType === ElementType.Trapezoid;
  }

  handleResize(context: ResizeContext) {
    const { element, coordinates, selectedAnchor } = context;
    const el = copy(element) as Trapezoid;
    const elAsRect = copy(element) as Rect;

    const oldWidth = Math.max(Math.abs(el.topWidth), Math.abs(el.bottomWidth));
    const [rawWidth, rawHeight, rawX, rawY] = resizeRect(
      selectedAnchor as ResizeAnchor,
      coordinates.x,
      coordinates.y,
      elAsRect
    );

    const newAbsWidth = Math.abs(rawWidth);
    const scaleFactor = oldWidth > 0 ? newAbsWidth / oldWidth : 1;
    const sign = rawWidth < 0 ? -1 : 1;
    const topWidth = Math.abs(el.topWidth) * scaleFactor * sign;
    const bottomWidth = Math.abs(el.bottomWidth) * scaleFactor * sign;
    const height = rawHeight;

    const computed = computeTrapezoidVertices(topWidth, bottomWidth, height);
    const width = computed ? computed.width : rawWidth;

    return {
      ...el,
      topWidth,
      bottomWidth,
      height,
      width,
      x: rawX,
      y: rawY,
    };
  }

  handleRotate(context: RotateContext) {
    return new RectHandler().handleRotate(context);
  }
}

/**
 * Shared by Triangle and Angle: an angle is a triangle with its base left
 * undrawn, so resize, rotate and apex drag read the exact same fields.
 */
class TriangleHandler implements ElementHandler {
  canHandle(elementType: ElementType): boolean {
    return [ElementType.Triangle, ElementType.Angle].includes(elementType);
  }

  handleResize(context: ResizeContext) {
    const { element, coordinates, selectedAnchor } = context;
    const el = copy(element) as Triangle;
    const elAsRect = copy(element) as Rect;

    const [width, height, x, y] = resizeRect(
      selectedAnchor as ResizeAnchor,
      coordinates.x,
      coordinates.y,
      elAsRect
    );

    return { ...el, width, height, x, y };
  }

  handleRotate(context: RotateContext) {
    return new RectHandler().handleRotate(context);
  }

  handleApexDrag(context: ApexDragContext) {
    const { element, coordinates } = context;
    const el = copy(element) as Triangle;

    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;

    const [localX] = rotateCenter(
      coordinates.x,
      coordinates.y,
      cx,
      cy,
      -el.rotate
    );

    const left = Math.min(el.x, el.x + el.width);
    const absWidth = Math.abs(el.width);
    const rawPointRatio = Math.max(0, Math.min(1, (localX - left) / absWidth));
    const flippedRatio = el.width < 0 ? 1 - rawPointRatio : rawPointRatio;
    const pointRatio = Math.round(flippedRatio / 0.01) * 0.01;

    return { ...el, pointRatio };
  }
}

class SymbolHandler implements ElementHandler {
  canHandle(elementType: ElementType): boolean {
    return elementType === ElementType.Symbol;
  }

  handleResize(context: ResizeContext) {
    const { element, coordinates, selectedAnchor } = context;
    const el = copy(element) as SymbolElement;

    const [width, height, x, y] = resizeRect(
      selectedAnchor as ResizeAnchor,
      coordinates.x,
      coordinates.y,
      el
    );
    const rectLen = Math.max(width, height);

    return {
      ...el,
      width: rectLen,
      height: rectLen,
      x,
      y,
    };
  }

  handleRotate(context: RotateContext) {
    return new RectHandler().handleRotate(context);
  }
}

class FixtureHandler implements ElementHandler {
  canHandle(elementType: ElementType): boolean {
    return elementType === ElementType.Fixture;
  }

  handleResize(context: ResizeContext) {
    const { element, coordinates, selectedAnchor } = context;
    const el = copy(element) as FixtureElement;

    const [width, height, x, y] = resizeRect(
      selectedAnchor as ResizeAnchor,
      coordinates.x,
      coordinates.y,
      el
    );

    return {
      ...el,
      width: width,
      height: height,
      x,
      y,
    };
  }

  handleRotate(context: RotateContext) {
    return new RectHandler().handleRotate(context);
  }
}

class PolylineHandler implements ElementHandler {
  canHandle(elementType: ElementType): boolean {
    return elementType === ElementType.Polyline;
  }

  handleResize(context: ResizeContext) {
    const {
      element,
      coordinates,
      selectedAnchor,
      isShiftPressed,
      isMetaPressed,
    } = context;

    if (element.type !== ElementType.Polyline) {
      throw new Error('Expected polyline element');
    }

    const newPoints = [...element.points];
    const pos = selectedAnchor as number;
    newPoints[pos].x = coordinates.x;
    newPoints[pos].y = coordinates.y;

    if (newPoints.length >= 2 && isShiftPressed) {
      if (newPoints.length === 2) {
        const adjacentIndex = getAdjacentPointIndices(newPoints, pos);

        const anchorPoint = newPoints[adjacentIndex[0]];

        const currentPoint = { x: coordinates.x, y: coordinates.y };
        const snapped = snapPointToAngle(anchorPoint, currentPoint);
        newPoints[pos].x = snapped.x;
        newPoints[pos].y = snapped.y;
      } else {
        const adjacentIndices = getAdjacentPointIndices(newPoints, pos);

        const p1 = newPoints[adjacentIndices[0]];
        const p2 = newPoints[adjacentIndices[1]];
        const currentPoint = { x: coordinates.x, y: coordinates.y };

        if (p1 && p2) {
          const snapped = closestAlignedPoint(p1, p2, currentPoint);
          newPoints[pos].x = snapped.x;
          newPoints[pos].y = snapped.y;
        }
      }
    }

    applySelfSnap(pos, newPoints, !isMetaPressed);
    syncConnectedPoints(newPoints);

    return {
      ...copy(element),
      points: newPoints,
    };
  }
}

export class ElementHandlerRegistry {
  private handlers: ElementHandler[] = [
    new TrapezoidHandler(),
    new TriangleHandler(),
    new RectHandler(),
    new SymbolHandler(),
    new FixtureHandler(),
    new PolylineHandler(),
  ];

  getHandler(elementType: ElementType): ElementHandler | null {
    return (
      this.handlers.find((handler) => handler.canHandle(elementType)) || null
    );
  }

  handleResize(elementType: ElementType, context: ResizeContext) {
    const handler = this.getHandler(elementType);
    if (!handler) {
      throw new Error(`No handler found for element type: ${elementType}`);
    }
    return handler.handleResize(context);
  }

  handleRotate(elementType: ElementType, context: RotateContext) {
    const handler = this.getHandler(elementType);
    if (!handler || !handler.handleRotate) {
      throw new Error(
        `No rotate handler found for element type: ${elementType}`
      );
    }
    return handler.handleRotate(context);
  }

  handleApexDrag(elementType: ElementType, context: ApexDragContext) {
    const handler = this.getHandler(elementType);
    if (!handler || !handler.handleApexDrag) {
      throw new Error(
        `No apex drag handler found for element type: ${elementType}`
      );
    }
    return handler.handleApexDrag(context);
  }
}

export const elementHandlerRegistry = new ElementHandlerRegistry();
